import React, { useEffect, useState } from "react"
import { useTrack } from "@hackler/react-sdk"
import usePlatform from "../../../hooks/usePlatform"
import useScrollImpressionLog from "../../../hooks/useScrollImpressionLog"
import { Box, Button, Grid, Typography } from "@material-ui/core"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import DemoRequestFormDialog from "../../../components/Dialog/DemoRequestFormDialog"
import { Locale } from "../../../locale"
import { FormattedMessage } from "react-intl"
import homepageAtf1 from "../../../assets/images/home/homepage_atf_1.png"
import homepageAtf2 from "../../../assets/images/home/homepage_atf_2.png"
import homepageAtf3 from "../../../assets/images/home/homepage_atf_3.png"
import homepageAtf4 from "../../../assets/images/home/homepage_atf_4.png"
import homepageAtf1Mo from "../../../assets/images/home/homepage_atf_1_mo.png"
import homepageAtf2Mo from "../../../assets/images/home/homepage_atf_2_mo.png"
import homepageAtf3Mo from "../../../assets/images/home/homepage_atf_3_mo.png"
import homepageAtf4Mo from "../../../assets/images/home/homepage_atf_4_mo.png"
import { enFontFamily } from "../../../layouts/MainFrame"
import useTour from "../../../hooks/useTour"
import ProgressButton from "../../../components/Button/ProgressButton"
import classNames from "classnames"

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    root: {
      background: "linear-gradient(284.04deg, #517FFF 0%, #234AB6 30%)",
    },
    containerBox: {
      position: "relative",
      margin: "0 auto",
      maxWidth: "1440px",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      flexWrap: "nowrap",
      [theme.breakpoints.up("md")]: {
        height: 720,
      },
    },
    textWrapper: {
      padding: "41px 20px 0",
      [theme.breakpoints.up("md")]: {
        padding: "50px 0 60px 0",
      },
    },
    title: {
      color: theme.palette.common.white,
      textAlign: "center",
      fontSize: 24,
      letterSpacing: -1,
      marginBottom: 14,
      [theme.breakpoints.up("md")]: {
        textAlign: "left",
        fontSize: 56,
        width: 660,
      },
    },
    caption: {
      marginTop: 10,
      color: "#fff",
      opacity: 0.8,
      fontSize: 14,
      fontFamily: enFontFamily,
      marginBottom: 8,
      [theme.breakpoints.up("md")]: {
        fontSize: 24,
        marginBottom: 0,
      },
    },
    hackle: {
      marginTop: 8,
      [theme.breakpoints.up("md")]: {
        marginTop: 40,
      },
    },
    buttonWrapper: {
      textAlign: "center",
      "& button": {
        textTransform: "none",
      },
      [theme.breakpoints.up("md")]: {
        textAlign: "inherit",
      },
    },
    button: {
      borderRadius: 8,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.main,
      fontSize: 14,
      marginRight: 4,
      padding: "8px 32px",
      "&:hover": {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.main,
        opacity: 0.8,
      },
      [theme.breakpoints.up("md")]: {
        fontSize: 20,
        marginRight: 4,
        padding: "12px 32px",
      },
    },
    buttonSmall: {
      color: theme.palette.common.white,
      padding: 4,
      fontSize: 14,
      margin: "-4px 4px 0 0",
      [theme.breakpoints.up("md")]: {
        padding: "12px 16px 12px 0",
        fontSize: 20,
        marginRight: 4,
      },
    },
    imageWrapper: {
      aspectRatio: "3/2",
      [theme.breakpoints.up("md")]: {
        width: "50%",
      },
    },
    mainImage: {
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "935px",
        height: "600px",
        position: "relative",
        left: "40px",
      },
    },
    mainImageB: {
      margin: "50px 0 40px",
      padding: "0 10px",
      width: "100%",
      position: "relative",
      [theme.breakpoints.up("md")]: {
        margin: 0,
        padding: 0,
        width: "100%",
      },
    },
    koMobileImage: {
      margin: "auto",
      padding: "35px 20px",
      "& img": {
        width: "100%",
      },
    },
    halfSize: {
      [theme.breakpoints.up("md")]: {
        width: "44%",
      },
    },
    imageWrapperB: {
      [theme.breakpoints.up("md")]: {
        width: "56%",
      },
    },
  })
)

export default function HeroSectionKo() {
  const track = useTrack()
  const { lang } = Locale.getDefault()
  const { isMobile } = usePlatform()
  const { ref } = useScrollImpressionLog({ message: "1: 배너", eventKey: "homepage_home_section_1_impression" })
  const tour = useTour()
  const [submitting, setSubmitting] = useState(false)
  const classes = useStyles({ lang })
  const images = [homepageAtf1, homepageAtf2, homepageAtf3, homepageAtf4]
  const mobileImages = [homepageAtf1Mo, homepageAtf2Mo, homepageAtf3Mo, homepageAtf4Mo]
  const [index, setIndex] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prevIndex) => (prevIndex === 3 ? 0 : prevIndex + 1))
    }, 1500)
    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <div ref={ref} className={classes.root}>
      <Box className={classes.containerBox}>
        <Grid container justify={"space-between"} className={classes.container} direction={isMobile ? "column" : "row"}>
          <Grid item container alignItems={"center"} className={classNames([classes.textWrapper, classes.halfSize])}>
            <Grid container direction={"column"}>
              <Grid item>
                <Typography className={classes.title}>
                  <strong>
                    데이터 기반 의사결정,
                    <br />
                    고속 성장의 지름길
                  </strong>
                  <Typography className={classes.caption}>
                    A/B 테스트부터 사용자 분석까지
                    <br />
                    실패없이 빠르게 성장해보세요
                  </Typography>
                </Typography>
              </Grid>
              <Grid item>
                <Box mt={isMobile ? 2 : 6}>
                  <Grid
                    container
                    className={classes.buttonWrapper}
                    spacing={isMobile ? 1 : 4}
                    direction={isMobile ? "column" : "row"}>
                    <Grid item>
                      <ProgressButton
                        progress={submitting}
                        onClick={() => {
                          setSubmitting(true)
                          track({
                            key: "homepage_tour_click",
                            properties: {
                              area: "section_1",
                            },
                          })

                          try {
                            //@ts-ignore
                            analytics.track("homepage_tour_click", { area: "hero_section" })
                          } catch (error) {}

                          tour()
                        }}
                        color={"primary"}
                        variant={"contained"}
                        className={classes.button}>
                        <strong>
                          <FormattedMessage id={"cta_tour"} />
                        </strong>
                      </ProgressButton>
                    </Grid>
                    <Grid item>
                      <DemoRequestFormDialog
                        ButtonElement={(handleButtonClick) => (
                          <Button
                            variant={"text"}
                            className={classes.buttonSmall}
                            onClick={() => {
                              track({ key: "homepage_home_section_1_request_demo_click" })

                              try {
                                //@ts-ignore
                                analytics.track("homepage_request_demo_click", { area: "hero_section" })
                              } catch (error) {}

                              handleButtonClick()
                            }}>
                            <strong>
                              <FormattedMessage id={"REQUEST_DEMO_TITLE"} />
                            </strong>
                          </Button>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            container
            className={classNames([classes.imageWrapper, classes.imageWrapperB])}
            justify="center"
            alignItems="center">
            <img
              className={classes.mainImageB}
              src={isMobile ? mobileImages[index] : images[index]}
              alt={"All-in-One Platform Hackle"}
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}
